import React, { useState, useEffect, useCallback } from 'react';
import { automunityAPI } from '../lib/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import toast, { Toaster } from 'react-hot-toast';
import './Deployments.css';

interface Lock {
  lock_id: number;
  application: string;
  environment: string;
  locked_by: string;
  locked_by_user_id: number;
  reason: string;
  locked_at: string;
  released_at: string | null;
}

const APPLICATIONS = [
  { key: 'it-rucksack', name: 'IT Rucksack', icon: '🎒' },
  { key: 'app-bakery', name: 'App Bakery', icon: '🧁' },
  { key: 'nexnow', name: 'NexNow', icon: '⚡' },
];

const ENVIRONMENTS = ['dev', 'production'];

const Deployments: React.FC = () => {
  const [locks, setLocks] = useState<Lock[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserName, setCurrentUserName] = useState('');
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [reviewRequests, setReviewRequests] = useState<any[]>([]);
  const [pendingTasks, setPendingTasks] = useState<any[]>([]);
  const [recentCommits, setRecentCommits] = useState<any[]>([]);

  const fetchLocks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await automunityAPI.getDeploymentLocks();
      const data = await res.body.json();
      setLocks(Array.isArray(data) ? data : (data as any)?.records || []);
    } catch (err) {
      console.error('Error fetching locks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocks();
    const resolve = async () => {
      try {
        const session = await fetchAuthSession();
        const email = (session.tokens?.idToken?.payload?.email as string || '').toLowerCase();
        const res = await automunityAPI.getUsers();
        const data = await res.body.json();
        const users = Array.isArray(data) ? data : (data as any)?.records || [];
        const match = users.find((u: any) => u.email?.toLowerCase() === email);
        if (match) { setCurrentUserName(match.full_name); setCurrentUserId(match.user_id); }
      } catch { /* no-op */ }
    };
    resolve();
    fetchPendingItems();
  }, [fetchLocks]);

  const fetchPendingItems = async () => {
    try {
      const [reqRes, clRes, taskRes] = await Promise.all([
        automunityAPI.getAllRequests(),
        automunityAPI.getChangelog(),
        automunityAPI.getPendingDeploymentTasks()
      ]);
      const reqData = await reqRes.body.json();
      const clData = await clRes.body.json();
      const taskData = await taskRes.body.json();
      const allReqs = Array.isArray(reqData) ? reqData : (reqData as any)?.records || [];
      // Genuinely deploy-relevant: awaiting a deploy, or deployed and awaiting verification.
      setPendingRequests(allReqs.filter((r: any) =>
        ['pending_deployment', 'deployed_in_review'].includes(r.status)
      ));
      // Done / in review — not yet queued for deployment. Shown separately so
      // the "Pending Deployment" count stays accurate.
      setReviewRequests(allReqs.filter((r: any) =>
        ['completed', 'completed_in_review'].includes(r.status)
      ));
      const allChangelog = Array.isArray(clData) ? clData : (clData as any)?.records || [];
      setRecentCommits(allChangelog.slice(0, 15));
      const tasks = Array.isArray(taskData) ? taskData : (taskData as any)?.records || [];
      setPendingTasks(tasks);
    } catch { /* no-op */ }
  };

  const getActiveLock = (app: string, env: string): Lock | undefined => {
    return locks.find(l => l.application === app && l.environment === env && !l.released_at);
  };

  const handleToggleLock = async (app: string, env: string) => {
    const existing = getActiveLock(app, env);
    if (existing) {
      // Only the person who locked it can unlock it
      if (existing.locked_by_user_id !== currentUserId) {
        toast.error(`Only ${existing.locked_by} can release this lock`);
        return;
      }
      try {
        await automunityAPI.releaseDeploymentLock(existing.lock_id, currentUserName);
        toast.success(`Released ${app} ${env} lock`);
        fetchLocks();
      } catch { toast.error('Failed to release lock'); }
    } else {
      // Lock it
      try {
        const res = await automunityAPI.createDeploymentLock({
          application: app,
          environment: env,
          locked_by: currentUserName,
          locked_by_user_id: currentUserId
        });
        const data = await res.body.json();
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        if (parsed.error) { toast.error(parsed.error); }
        else { toast.success(`Locked ${app} ${env}`); fetchLocks(); }
      } catch { toast.error('Failed to acquire lock'); }
    }
  };

  const handleNudge = async (e: React.MouseEvent, lock: Lock, appName: string) => {
    e.stopPropagation();
    try {
      const res = await automunityAPI.nudgeLockHolder(lock.lock_id, currentUserName);
      const data = await res.body.json();
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      if (parsed.error) { toast.error(parsed.error); }
      else { toast.success(`Nudged ${lock.locked_by} about ${appName} ${lock.environment}`); }
    } catch { toast.error('Failed to send nudge'); }
  };

  const formatTimeSince = (dateStr: string): string => {
    // DB stores UTC — append Z if not present so JS parses correctly
    const utcDate = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const diff = Date.now() - new Date(utcDate).getTime();
    if (diff < 0) return 'just now';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
    if (hours > 0) return `${hours}h ${mins}m ago`;
    return `${mins}m ago`;
  };

  if (loading) return <div className="deployments-loading">Loading deployment locks...</div>;

  return (
    <>
      <Toaster position="top-right" />
      <div className="deployments-page">
        <div className="deployments-header">
          <h1>🚀 Deployments</h1>
          <p className="header-subtitle">Click an environment to lock it before deploying. Only you can unlock your own locks.</p>
        </div>

        <div className="lock-grid">
          {APPLICATIONS.map(app => (
            <div key={app.key} className="app-card">
              <div className="app-card-header">
                <span className="app-icon">{app.icon}</span>
                <h3>{app.name}</h3>
              </div>
              <div className="env-rows">
                {ENVIRONMENTS.map(env => {
                  const lock = getActiveLock(app.key, env);
                  const isLockedByMe = lock && lock.locked_by_user_id === currentUserId;
                  const isLockedByOther = lock && lock.locked_by_user_id !== currentUserId;

                  return (
                    <div
                      key={env}
                      className={`env-row ${lock ? (isLockedByMe ? 'locked-mine' : 'locked-other') : 'unlocked'}`}
                      onClick={() => handleToggleLock(app.key, env)}
                      style={{ cursor: isLockedByOther ? 'not-allowed' : 'pointer' }}
                    >
                      <div className="env-info">
                        <span className="env-name">{env}</span>
                        {lock ? (
                          <span className="env-status">
                            🔒 {lock.locked_by} — {formatTimeSince(lock.locked_at)}
                          </span>
                        ) : (
                          <span className="env-status available">Available</span>
                        )}
                      </div>
                      <div className="env-action">
                        {!lock && <span className="lock-btn">🔓 Click to Lock</span>}
                        {isLockedByMe && <span className="unlock-btn">🔓 Unlock</span>}
                        {isLockedByOther && (
                          <>
                            <button
                              className="nudge-btn"
                              onClick={(e) => handleNudge(e, lock!, app.name)}
                              title={`Nudge ${lock!.locked_by} on Teams`}
                            >
                              👋 Nudge
                            </button>
                            <span className="locked-indicator">🔒</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Open Tasks Pending Deployment — the "what must happen before we ship" list */}
        <div className="deploy-section">
          <h2>
            ✅ Open Tasks Pending Deployment ({pendingTasks.length})
            {pendingTasks.some((t: any) => t.blocks_production) && (
              <span className="prod-blocking-count" style={{ color: '#dc3545', fontSize: '0.7em', marginLeft: 8 }}>
                {pendingTasks.filter((t: any) => t.blocks_production).length} block production
              </span>
            )}
          </h2>
          <p className="header-subtitle" style={{ margin: '0 0 10px' }}>
            Outstanding tasks on requests that are pending deployment or deployed and in review — the manual steps to finish before/after shipping.
          </p>
          {pendingTasks.length > 0 ? (
            <div className="deploy-list">
              {pendingTasks.map((t: any) => (
                <div
                  key={t.task_id}
                  className={`deploy-item task-item ${t.blocks_production ? 'task-blocks-prod' : ''}`}
                  onClick={() => window.open(`/requests/${t.request_id}`, '_blank')}
                  style={{ cursor: 'pointer', ...(t.blocks_production ? { borderLeft: '3px solid #dc3545' } : {}) }}
                >
                  <span className="item-id">#{t.request_id}</span>
                  <span className="item-title">
                    {t.title}
                    <span style={{ display: 'block', fontSize: '11px', color: '#888' }}>
                      {t.request_title}
                    </span>
                  </span>
                  <span className="item-meta">{t.automation_name || '—'}</span>
                  <span className="task-flags" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {!!t.blocks_production && (
                      <span className="item-badge" style={{ background: '#dc3545', color: '#fff' }}>blocks prod</span>
                    )}
                    {!!t.requires_human && (
                      <span className="item-badge" style={{ background: '#e2e8f0', color: '#2d3748' }}>human</span>
                    )}
                    {t.environment && (
                      <span className="item-badge" style={{ background: '#e7f1ff', color: '#0b5ed7' }}>{t.environment}</span>
                    )}
                    {t.category && t.category !== 'general' && (
                      <span className="item-badge" style={{ background: '#f0f0f0', color: '#555' }}>{t.category}</span>
                    )}
                  </span>
                  <span className={`item-badge status-${t.request_status}`}>{t.request_status?.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state">No open tasks on requests pending deployment 🎉</p>
          )}
        </div>

        {/* Pending Requests for Deployment (accurate: only deploy-relevant statuses) */}
        <div className="deploy-section">
          <h2>📋 Pending Deployment ({pendingRequests.length})</h2>
          {pendingRequests.length > 0 ? (
            <div className="deploy-list">
              {pendingRequests.map((r: any) => (
                <div key={r.request_id} className="deploy-item" onClick={() => window.open(`/requests/${r.request_id}`, '_blank')} style={{ cursor: 'pointer' }}>
                  <span className="item-id">#{r.request_id}</span>
                  <span className="item-title">{r.title}</span>
                  <span className="item-meta">{r.automation_name || '—'}</span>
                  <span className={`item-badge status-${r.status}`}>{r.status?.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state">No requests pending deployment</p>
          )}
        </div>

        {/* Completed / In Review — done but not yet queued for deployment */}
        <div className="deploy-section">
          <h2>🧪 In Review — not yet queued ({reviewRequests.length})</h2>
          {reviewRequests.length > 0 ? (
            <div className="deploy-list">
              {reviewRequests.map((r: any) => (
                <div key={r.request_id} className="deploy-item" onClick={() => window.open(`/requests/${r.request_id}`, '_blank')} style={{ cursor: 'pointer' }}>
                  <span className="item-id">#{r.request_id}</span>
                  <span className="item-title">{r.title}</span>
                  <span className="item-meta">{r.automation_name || '—'}</span>
                  <span className={`item-badge status-${r.status}`}>{r.status?.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state">Nothing in review</p>
          )}
        </div>

        {/* Recent Commits grouped by repository */}
        <div className="deploy-section">
          <h2>📝 Recent Commits</h2>
          {recentCommits.length > 0 ? (
            <>
              {Array.from(new Set(recentCommits.map((c: any) => c.repository || 'unknown'))).map(repo => {
                const repoCommits = recentCommits.filter((c: any) => (c.repository || 'unknown') === repo);
                return (
                  <div key={repo} className="repo-group">
                    <h4 className="repo-name">📁 {repo} ({repoCommits.length})</h4>
                    <div className="deploy-list">
                      {repoCommits.map((c: any, i: number) => (
                        <div key={i} className="deploy-item">
                          <code className="item-hash">{(c.commit_id || '').slice(0, 7)}</code>
                          <span className="item-title">{c.commit_message?.split('\n')[0] || 'No message'}</span>
                          <span className="item-meta">{c.commit_author}</span>
                          <span className="item-meta">{c.commit_date ? new Date(Number(c.commit_date) * 1000).toLocaleDateString() : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <p className="empty-state">No recent commits found</p>
          )}
        </div>
      </div>
    </>
  );
};

export default Deployments;
